import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, RotateCcw, Archive } from "lucide-react";

export default function DeletedAccountsBin() {
  const [adminBin, setAdminBin] = useState([]);
  const [selfDeleted, setSelfDeleted] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [{ data: bin }, { data: self }] = await Promise.all([
        api.get("/admin/deleted-users"),
        api.get("/admin/self-deleted-users"),
      ]);
      setAdminBin(bin);
      setSelfDeleted(self);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const restoreFromBin = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/deleted-users/${id}/restore`);
      toast.success("Account restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const purgeFromBin = async (id, name) => {
    if (!window.confirm(`Permanently delete ${name}? This removes all their data and cannot be undone.`)) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/deleted-users/${id}`);
      toast.success("Account permanently deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const restoreSelfDeleted = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/self-deleted-users/${id}/restore`);
      toast.success("Account restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="deleted-accounts-bin" className="mt-12">
      <h2 className="font-serif-display text-2xl mb-4 inline-flex items-center gap-2">
        <Archive className="w-5 h-5" style={{ color: "var(--text-secondary)" }} /> Deleted accounts
      </h2>

      <div className="mb-8">
        <div className="label-eyebrow mb-3">Admin bin — restorable or permanently deletable up to 45 days</div>
        {adminBin.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Nothing in the bin.</p>
        ) : (
          <div className="space-y-2" data-testid="admin-bin-list">
            {adminBin.map(u => (
              <div key={u.id} data-testid={`admin-bin-row-${u.id}`}
                className="card-soft p-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{u.name} <span className="text-xs capitalize" style={{ color: "var(--text-secondary)" }}>· {u.role}</span></div>
                  <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                    {u.email} · deleted {new Date(u.deleted_at).toLocaleDateString()} · {u.days_remaining} day{u.days_remaining === 1 ? "" : "s"} left
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`admin-bin-restore-${u.id}`} disabled={busyId === u.id}
                    onClick={() => restoreFromBin(u.id)}
                    className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1">
                    <RotateCcw className="w-3.5 h-3.5" /> Restore
                  </button>
                  <button data-testid={`admin-bin-purge-${u.id}`} disabled={busyId === u.id}
                    onClick={() => purgeFromBin(u.id, u.name)}
                    className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
                    style={{ borderColor: "var(--accent)", color: "var(--accent-hover)" }}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete permanently
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="label-eyebrow mb-3">Self-deleted accounts — auto-restore on login (0-15d), email request (15-30d)</div>
        {selfDeleted.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>None pending.</p>
        ) : (
          <div className="space-y-2" data-testid="self-deleted-list">
            {selfDeleted.map(u => (
              <div key={u.id} data-testid={`self-deleted-row-${u.id}`}
                className="card-soft p-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{u.name} <span className="text-xs capitalize" style={{ color: "var(--text-secondary)" }}>· {u.role}</span></div>
                  <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                    {u.email} · deleted {new Date(u.deleted_at).toLocaleDateString()} ·{" "}
                    {u.auto_restorable ? "can still self-restore by logging in" : "past self-restore window"} ·{" "}
                    {u.days_remaining} day{u.days_remaining === 1 ? "" : "s"} until permanent purge
                  </div>
                  {u.delete_reason && (
                    <div className="text-xs mt-1 italic" style={{ color: "var(--text-secondary)" }}>Reason: {u.delete_reason}</div>
                  )}
                </div>
                <button data-testid={`self-deleted-restore-${u.id}`} disabled={busyId === u.id}
                  onClick={() => restoreSelfDeleted(u.id)}
                  className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1">
                  <RotateCcw className="w-3.5 h-3.5" /> Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
